import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { DepartureBoard } from "@/components/DepartureBoard";
import { BookingDialog } from "@/components/BookingDialog";
import { DriverBadges } from "@/components/DriverBadges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { shareDriverToWA } from "@/lib/shareSchedule";
import NotFound from "@/pages/NotFound";
import { Star, Quote, Share2, MapPin, Users } from "lucide-react";

const CAT_LABELS = {
  timeliness: "Ketepatan Waktu",
  cleanliness: "Kebersihan",
  friendliness: "Keramahan",
  comfort: "Kenyamanan",
  safety: "Keamanan",
  responsiveness: "Respons Pelayanan",
};

export default function DriverProfile() {
  const { driverId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/drivers/${driverId}/profile`)
      .then((r) => {
        setData(r.data);
        document.title = `${r.data.driver.business_name} — Profil Driver Travele`;
      })
      .catch(() => setNotFound(true));
  }, [driverId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Tautan profil disalin");
    } catch {
      toast.error("Gagal menyalin tautan");
    }
  };

  if (notFound) return <NotFound message="Profil driver tidak ditemukan." />;

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-7xl px-4 py-20 text-muted-foreground">Memuat...</div>
      </div>
    );
  }

  const { driver, vehicles, schedules, reviews, category_averages } = data;
  const memberSince = driver.created_at ? new Date(driver.created_at).getFullYear() : null;

  return (
    <div className="min-h-screen bg-background" data-testid="driver-profile-page">
      <Seo
        path={`/driver/${driver.slug || driverId}`}
        title={`${driver.business_name} — Profil Driver Travele`}
        description={`Profil driver ${driver.business_name}${driver.city ? ` di ${driver.city}` : ""} — rating ${driver.avg_rating || "baru"}, ${driver.rating_count} ulasan, ${driver.total_trips} perjalanan. Lihat armada & jadwal aktif di Travele.`}
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: driver.business_name,
          ...(driver.city ? { address: { "@type": "PostalAddress", addressLocality: driver.city } } : {}),
          ...(driver.rating_count > 0 ? { aggregateRating: { "@type": "AggregateRating", ratingValue: driver.avg_rating, reviewCount: driver.rating_count } } : {}),
        }}
      />
      <Navbar />

      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
          <div className="flex flex-wrap items-start gap-5">
            <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-primary/15 font-display text-2xl font-black text-primary" data-testid="driver-avatar">
              {driver.business_name.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1">
              <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl" data-testid="driver-name">{driver.business_name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {driver.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-primary" /> {driver.city}</span>}
                {memberSince && <span>Mitra sejak {memberSince}</span>}
              </div>
              <div className="mt-3">
                <DriverBadges verified={driver.verified} priority={driver.priority} rating={driver.avg_rating} reviews={driver.rating_count} trips={driver.total_trips} testId="profile-badges" />
              </div>
              {(driver.routes || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="driver-routes">
                  <span className="text-xs text-muted-foreground">Rute dilayani:</span>
                  {driver.routes.slice(0, 8).map((r) => (
                    <span key={r} className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[11px] text-foreground">{r}</span>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={() => shareDriverToWA(driver)} className="gap-2 bg-[#25D366] font-bold text-black hover:bg-[#25D366]/90" data-testid="share-profile-btn">
              <Share2 className="h-4 w-4" /> Bagikan ke WhatsApp
            </Button>
            <Button onClick={copyLink} variant="secondary" className="gap-2" data-testid="copy-profile-btn">Salin Tautan</Button>
          </div>

          {/* stat strip */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="border border-border bg-background p-4" data-testid="stat-rating">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Rating</div>
              <div className="mt-1 flex items-center gap-1 font-mono-terminal text-2xl font-bold text-primary">
                {driver.avg_rating || "Baru"} <Star className="h-5 w-5 fill-primary" />
              </div>
            </div>
            <div className="border border-border bg-background p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Ulasan</div>
              <div className="mt-1 font-mono-terminal text-2xl font-bold">{driver.rating_count}</div>
            </div>
            <div className="border border-border bg-background p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Perjalanan</div>
              <div className="mt-1 font-mono-terminal text-2xl font-bold">{driver.total_trips}</div>
            </div>
            <div className="border border-border bg-background p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Armada</div>
              <div className="mt-1 font-mono-terminal text-2xl font-bold">{vehicles.length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        {/* Category ratings */}
        {Object.keys(category_averages || {}).length > 0 && (
          <>
            <h2 className="mb-4 font-display text-2xl font-bold tracking-tight">Penilaian per Kategori</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(category_averages).map(([k, v]) => (
                <div key={k} className="border border-border bg-card p-4" data-testid={`cat-${k}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{CAT_LABELS[k] || k}</span>
                    <span className="font-mono-terminal text-sm font-bold text-primary">{v.toFixed(1)}</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary" style={{ width: `${(v / 5) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Armada */}
        <h2 className="mb-4 mt-10 font-display text-2xl font-bold tracking-tight">Armada</h2>
        {vehicles.length === 0 ? (
          <p className="text-muted-foreground">Belum ada armada terdaftar.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((v) => (
              <div key={v.id} className="overflow-hidden border border-border bg-card" data-testid={`profile-vehicle-${v.id}`}>
                {v.photo ? (
                  <img src={fileUrl(v.photo)} alt={v.name} loading="lazy" className="h-40 w-full object-cover" />
                ) : (
                  <div className="flex h-40 w-full items-center justify-center bg-muted/30 text-xs text-muted-foreground">Belum ada foto</div>
                )}
                <div className="p-4">
                  <div className="font-semibold">{v.name}</div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground"><Users className="h-3.5 w-3.5" /> {v.capacity} kursi · {v.brand} {v.model}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(v.facilities || []).map((f) => <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Jadwal aktif */}
        <h2 className="mb-4 mt-10 font-display text-2xl font-bold tracking-tight">Jadwal Keberangkatan Aktif</h2>
        <DepartureBoard schedules={schedules} onBook={setBooking} showRoute={true} />

        {/* Ulasan */}
        {reviews.length > 0 && (
          <>
            <h2 className="mb-4 mt-12 font-display text-2xl font-bold tracking-tight">Ulasan Penumpang</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {reviews.map((rv, i) => (
                <div key={i} className="border border-border bg-card p-5" data-testid={`profile-review-${i}`}>
                  <Quote className="h-5 w-5 text-primary/50" />
                  <div className="mt-2 flex items-center gap-1 text-primary">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s < Math.round(rv.average) ? "fill-primary" : "text-muted-foreground"}`} />
                    ))}
                    <span className="ml-1 font-mono-terminal text-xs text-muted-foreground">{rv.average?.toFixed(1)}</span>
                  </div>
                  {rv.comment && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">"{rv.comment}"</p>}
                  <div className="mt-3 text-sm font-semibold">{rv.passenger_name}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
      <WhatsAppFloat />
      <BookingDialog schedule={booking} open={!!booking} onClose={() => setBooking(null)} />
    </div>
  );
}
